/**
 * What the form looks like once it knows who is filling it in.
 *
 * Three states, and a door moves between them rather than rebuilding the form:
 * unknown, the account exists, the address is new. Every field is drawn either way
 * because RegistrationSpec decides that, so a state is a matter of which extras are
 * revealed, what the password box is asking for, and what the button does next.
 *
 * The prefill is why `existing` takes the lookup's answer: someone who already has
 * a profile should not retype it to join something. The name is the exception and is
 * locked, because it is the one field not written back from here.
 *
 * The door supplies the three handlers, since what a click means is its own
 * business, and owns whatever else a reset has to undo beyond the form.
 */

const SECTIONS = {
    passwordFields: '#passwordFields',
    passwordConfirmationField: '#passwordConfirmationField',
    recaptcha: '#recaptcha',
    loginCodeField: '#loginCodeFieldRegister',
    validEmailMessage: '#validEmailMessage',
    invalidEmailMessage: '#invalidEmailMessage',
    forgotPassword: '#forgotPassword',
    forgotPasswordLink: '#forgotPasswordLink'
};

const INPUTS = {
    name: '#name',
    password: '#password',
    passwordConfirmation: '#password_confirmation',
    loginCode: '#login_code_register',
    passwordError: '#passwordError'
};

/** Written straight in from the account. */
const PROFILE = {
    user_company: '#user_company',
    user_company_role: '#user_company_role',
    user_city: '#user_city',
    linkedin_url: '#linkedin_url'
};

/** select2 draws its own box, so the <select> has to announce the change. */
const PROFILE_WIDGETS = {
    user_residence_country: '#user_residence_country'
};

export function formState({ form, submitButton, onCheck, onLogin, onRegister, labels } = {}) {
    const $form = $(form);
    const $button = $(submitButton);

    // The two words the button can carry. They come from the door because they are
    // copy, and one door's "Register" is another's "Get Access".
    const word = Object.assign({ register: 'Register', login: 'Login' }, labels || {});

    const sections = {};
    const inputs = {};

    Object.keys(SECTIONS).forEach((key) => { sections[key] = $(SECTIONS[key]); });
    Object.keys(INPUTS).forEach((key) => { inputs[key] = $(INPUTS[key]); });

    const greetedName = sections.validEmailMessage.find('strong');

    function show(name, shouldShow) {
        const section = sections[name];

        if (! section || ! section.length) {
            return;
        }

        if (shouldShow) {
            section.show();
        } else {
            section.hide();
        }
    }

    /** Named sections are shown, every other one hidden: a state is the whole list. */
    function reveal(shown) {
        Object.keys(SECTIONS).forEach((name) => show(name, shown.indexOf(name) !== -1));
    }

    function setSubmitHandler(handler, label) {
        // A button marked data-lk-fixed-label names the ask, not the step: the
        // join wizard's "Confirm Seat Request" stays put whether the click ends
        // up creating an account or signing an existing one in.
        if (! $button.is('[data-lk-fixed-label]')) {
            $button.text(label || '');
        }

        $button.off('click').on('click', handler);
    }

    // The shared password input doubles as "create" (new account) and "enter"
    // (existing account): placeholder and autocomplete must follow the mode,
    // otherwise the login state reads as password creation. The register
    // placeholder is cached off the markup on first swap; the login one comes
    // from data-placeholder-login (Blade owns the translations).
    function passwordAsks(forExistingAccount) {
        const input = inputs.password;

        if (! input.data('placeholderRegister')) {
            input.data('placeholderRegister', input.attr('placeholder'));
        }

        input.attr('placeholder', forExistingAccount
            ? (input.data('placeholderLogin') || input.data('placeholderRegister'))
            : input.data('placeholderRegister'));
        input.attr('autocomplete', forExistingAccount ? 'current-password' : 'new-password');
    }

    function clearPasswordError() {
        inputs.passwordError.hide().text('');
        inputs.password.removeClass('is-invalid');
    }

    // Scoped to the form on purpose: a chapter page carries a second #linkedin_url
    // outside it, and a document-wide lookup writes the profile into that one.
    function field(selector) {
        return $form.find(selector);
    }

    function prefill(data) {
        Object.keys(PROFILE).forEach((key) => {
            if (data[key]) {
                field(PROFILE[key]).val(data[key]);
            }
        });

        Object.keys(PROFILE_WIDGETS).forEach((key) => {
            if (data[key]) {
                field(PROFILE_WIDGETS[key]).val(data[key]).trigger('change');
            }
        });

        // The organization type is the one field the account does not win. Where the
        // join wizard locked it, the person has just read the requirements for the
        // type they picked, and quietly swapping it would make that page a lie. The
        // lock renders a hidden input rather than a <select>, which is how this
        // tells the two apart.
        const organizationType = field('#user_organization_type');

        if (data.user_organization_type && organizationType.is('select')) {
            organizationType.val(data.user_organization_type).trigger('change');
        }
    }

    return {
        /** Nobody identified yet: the address, and a button that looks them up. */
        reset() {
            reveal([]);

            inputs.password.val('');
            inputs.passwordConfirmation.val('');
            inputs.loginCode.val('');

            // Typing a different address puts the form back to an unknown person, so
            // the name stops being the account's and becomes theirs to fill again.
            inputs.name.prop('readonly', false);

            clearPasswordError();
            setSubmitHandler(onCheck, word.register);
        },

        existing(data) {
            passwordAsks(true);

            const firstName = (data.name || '').trim().split(/\s+/)[0] || '';

            if (greetedName.length) {
                greetedName.text(firstName);
            }

            // The name is the account's, and unlike the rest of the profile it is not
            // written back from here: an editable box that greets you as someone else
            // and then discards what you type is worse than no box at all.
            if (data.name) {
                inputs.name.val(data.name).prop('readonly', true);
            }

            prefill(data);

            reveal(['validEmailMessage', 'passwordFields', 'forgotPassword', 'forgotPasswordLink']);
            setSubmitHandler(onLogin, word.login);
        },

        fresh() {
            passwordAsks(false);

            // Consent is drawn with the fields rather than revealed here: it is
            // asked on every path, signing in included.
            reveal([
                'invalidEmailMessage',
                'passwordFields',
                'passwordConfirmationField',
                'recaptcha'
            ]);
            setSubmitHandler(onRegister, word.register);
        },

        /** A requested login code replaces the password box with the code box. */
        awaitingLoginCode() {
            reveal(['loginCodeField']);
            inputs.loginCode.val('');
        },

        setSubmitHandler,
        clearPasswordError,

        valid() {
            if (! $form.length) {
                return false;
            }

            return typeof $form.valid === 'function' ? $form.valid() : true;
        }
    };
}
